import type { TrainingTemplate, Card } from "../../../../api/training/Training.typed";

export type TrainingTemplatesEditorState = {
  items: TrainingTemplate[];
  selectedId: number | null;
  draft: TrainingTemplate | null;
};

export const trainingTemplatesEditorInitialState: TrainingTemplatesEditorState = {
  items: [],
  selectedId: null,
  draft: null,
};

export type TemplateEditorAction =
  | { type: "LOAD_ITEMS"; items: TrainingTemplate[] }
  | { type: "SELECT"; id: number | null }
  | {
      type: "UPDATE_FIELD";
      field: keyof Omit<TrainingTemplate, "ID">;
      value: string | number | boolean | null;
    }
  | { type: "ADD_CARD"; cardName: string }
  | { type: "REMOVE_CARD"; index: number }
  | { type: "COMMIT_DRAFT" }
  | { type: "PATCH_ITEM"; item: TrainingTemplate }
  | { type: "REMOVE_ITEM"; id: number };

function cloneTemplate(template: TrainingTemplate): TrainingTemplate {
  return {
    ...template,
    Cards: { Card: [...(template.Cards?.Card ?? [])] },
    TrainingCourses: template.TrainingCourses ? [...template.TrainingCourses] : undefined,
  };
}

function draftFromItems(
  items: TrainingTemplate[],
  id: number | null,
): Pick<TrainingTemplatesEditorState, "selectedId" | "draft"> {
  if (id == null) {
    return { selectedId: null, draft: null };
  }

  const found = items.find((item) => item.ID === id);
  if (!found) {
    return { selectedId: null, draft: null };
  }

  return { selectedId: id, draft: cloneTemplate(found) };
}

function replaceItem(items: TrainingTemplate[], item: TrainingTemplate): TrainingTemplate[] {
  const exists = items.some((entry) => entry.ID === item.ID);
  if (!exists) {
    return [...items, cloneTemplate(item)];
  }

  return items.map((entry) => (entry.ID === item.ID ? cloneTemplate(item) : entry));
}

export function trainingTemplatesEditorReducer(
  state: TrainingTemplatesEditorState,
  action: TemplateEditorAction,
): TrainingTemplatesEditorState {
  switch (action.type) {
    case "LOAD_ITEMS":
      return {
        items: action.items,
        ...draftFromItems(action.items, state.selectedId),
      };
    case "SELECT":
      if (action.id === state.selectedId) {
        return state;
      }

      return { ...state, ...draftFromItems(state.items, action.id) };
    case "UPDATE_FIELD":
      if (!state.draft) {
        return state;
      }

      return { ...state, draft: { ...state.draft, [action.field]: action.value } };
    case "ADD_CARD":
      if (!state.draft) {
        return state;
      }

      return {
        ...state,
        draft: {
          ...state.draft,
          Cards: {
            Card: [...(state.draft.Cards?.Card ?? []), { Organisation: action.cardName }],
          },
        },
      };
    case "REMOVE_CARD":
      if (!state.draft) {
        return state;
      }

      return {
        ...state,
        draft: {
          ...state.draft,
          Cards: {
            Card: (state.draft.Cards?.Card ?? []).filter(
              (_: Card, i: number) => i !== action.index,
            ),
          },
        },
      };
    case "COMMIT_DRAFT":
      if (!state.draft) {
        return state;
      }

      return { ...state, items: replaceItem(state.items, state.draft) };
    case "PATCH_ITEM": {
      const items = replaceItem(state.items, action.item);
      if (state.selectedId !== action.item.ID) {
        return { ...state, items };
      }

      return { items, selectedId: action.item.ID, draft: cloneTemplate(action.item) };
    }
    case "REMOVE_ITEM": {
      const items = state.items.filter((item) => item.ID !== action.id);
      if (state.selectedId !== action.id) {
        return { ...state, items };
      }

      return { items, selectedId: null, draft: null };
    }
    default:
      return state;
  }
}
